import type { CustomAttribute, CustomDataType } from './config';
import { slugify } from './text';

/**
 * Custom natural-language attributes are resolved deterministically whenever the question maps
 * to data we already collect (hiring, delivery, branches, people by role). AI is only used for
 * the remaining questions, and its answers are always labelled as AI output.
 */
export const ROLE_FAMILIES = {
  hr: {
    label: 'HR / people',
    pattern:
      /\b(hr|h\.r\.|human resources?|people (?:operations|ops|partner|team)|talent (?:acquisition|manager)|recruit(?:er|ment|ing)|chief people officer|head of people)\b|موارد بشري|الموارد البشري|توظيف/i,
  },
  marketing: {
    label: 'Marketing',
    pattern: /\b(marketing|cmo|growth|brand manager|head of brand|digital marketing|communications|social media manager)\b|تسويق/i,
  },
  sales: {
    label: 'Sales / business development',
    pattern: /\b(sales|business development|bizdev|cro|commercial director|account executive|key account)\b|مبيعات|تطوير الاعمال/i,
  },
  executive: {
    label: 'Founder / executive',
    pattern:
      /\b(ceo|chief executive|founder|co-?founder|managing director|general manager|owner|president|managing partner)\b|المدير العام|مدير عام|المؤسس|الرئيس التنفيذي/i,
  },
  technology: {
    label: 'Technology / IT',
    pattern: /\b(cto|chief technology|it manager|head of it|engineering manager|technical director|head of technology)\b/i,
  },
  finance: {
    label: 'Finance',
    pattern: /\b(cfo|chief financial|finance manager|financial controller|head of finance|accounting manager)\b|مالي/i,
  },
  operations: {
    label: 'Operations',
    pattern: /\b(coo|chief operating|operations manager|head of operations)\b|العمليات/i,
  },
  procurement: {
    label: 'Procurement',
    pattern: /\b(procurement|purchasing|buyer|supply chain)\b|مشتريات/i,
  },
} as const;

export type RoleFamily = keyof typeof ROLE_FAMILIES;

export function matchRoleFamilies(title: string | null | undefined): RoleFamily[] {
  if (!title) return [];
  return (Object.keys(ROLE_FAMILIES) as RoleFamily[]).filter((family) => ROLE_FAMILIES[family].pattern.test(title));
}

export type CustomIntent =
  | { kind: 'hiring' }
  | { kind: 'delivery' }
  | { kind: 'branch_count' }
  | { kind: 'multi_city' }
  | { kind: 'find_person'; role: RoleFamily }
  | { kind: 'generic' };

const FIND_PREFIX = /^\s*(find|identify|get|who is|who's|locate|list)\b/i;

/** Map a natural-language instruction to a deterministic strategy when one exists. */
export function detectCustomIntent(instruction: string, dataType: CustomDataType): CustomIntent {
  const text = instruction.toLowerCase();
  if (/(hiring|job postings?|job openings?|open (?:positions|roles|jobs|vacancies)|vacanc|recruiting|careers? page)/.test(text)) {
    if (dataType === 'boolean' || dataType === 'number' || dataType === 'text') return { kind: 'hiring' };
  }
  if (/\bdeliver(y|s|ies)?\b|توصيل|دليفري/.test(text) && (dataType === 'boolean' || dataType === 'text')) {
    return { kind: 'delivery' };
  }
  if (/(how many|number of|count of)\s+(branches|locations|outlets|stores|offices)|\bbranch(es)?\b/.test(text) && dataType === 'number') {
    return { kind: 'branch_count' };
  }
  if (/(more than one|multiple|several|many|two or more)\s+cit(y|ies)|operate in .*cities/.test(text)) {
    return { kind: 'multi_city' };
  }
  if (FIND_PREFIX.test(text) || /(decision maker|manager|director|head of|contact person)/.test(text)) {
    const role = (Object.keys(ROLE_FAMILIES) as RoleFamily[]).find((family) => ROLE_FAMILIES[family].pattern.test(instruction));
    if (role) return { kind: 'find_person', role };
  }
  return { kind: 'generic' };
}

export function describeIntent(intent: CustomIntent): string {
  switch (intent.kind) {
    case 'hiring':
      return 'Answered from LinkedIn open jobs and the company careers page (deterministic).';
    case 'delivery':
      return 'Answered from Google Maps service attributes (deterministic).';
    case 'branch_count':
      return 'Counted from listings of the same brand in this dataset and LinkedIn locations (lower bound).';
    case 'multi_city':
      return 'Answered from the distinct cities of the brand’s discovered locations (deterministic).';
    case 'find_person':
      return `Matched against discovered people by job title (${ROLE_FAMILIES[intent.role].label}). People are never invented.`;
    case 'generic':
      return 'Answered by AI from collected evidence when enabled; labelled as AI-inferred.';
  }
}

/** Build a custom attribute definition from the "Add custom attribute" form. */
export function makeCustomAttribute(name: string, instruction: string, dataType: CustomDataType, aiAssisted = true): CustomAttribute {
  const slug = slugify(name).replace(/[^a-z0-9_]/g, '').slice(0, 48) || `field_${Date.now().toString(36)}`;
  return { key: `custom.${slug}`, name: name.trim(), instruction: instruction.trim(), dataType, aiAssisted };
}
